// ============================================
// Background3D.js — Interactive 3D floating
//   geometry canvas that reacts to mouse
// ============================================
import React, { useEffect, useRef } from 'react';
import '../styles/Background3D.css';

const Background3D = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    let animId;
    let mouseX = 0, mouseY = 0;
    let time = 0;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const onMouse = (e) => {
      mouseX = (e.clientX / window.innerWidth  - 0.5) * 2;
      mouseY = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMouse);

    // --- 3D projection helpers ---
    const project = (x, y, z, fov = 500) => {
      const scale = fov / (fov + z);
      return {
        x: canvas.width  / 2 + x * scale,
        y: canvas.height / 2 + y * scale,
        scale,
      };
    };

    const rotateX = (y, z, angle) => ({
      y: y * Math.cos(angle) - z * Math.sin(angle),
      z: y * Math.sin(angle) + z * Math.cos(angle),
    });

    const rotateY = (x, z, angle) => ({
      x: x * Math.cos(angle) + z * Math.sin(angle),
      z: -x * Math.sin(angle) + z * Math.cos(angle),
    });

    // --- Build wireframe cube vertices + edges ---
    const makeCube = (cx, cy, cz, size) => {
      const h = size / 2;
      const verts = [
        [-h,-h,-h],[h,-h,-h],[h,h,-h],[-h,h,-h],
        [-h,-h, h],[h,-h, h],[h,h, h],[-h,h, h],
      ].map(([x,y,z]) => ({ x: cx+x, y: cy+y, z: cz+z }));
      const edges = [
        [0,1],[1,2],[2,3],[3,0],
        [4,5],[5,6],[6,7],[7,4],
        [0,4],[1,5],[2,6],[3,7],
      ];
      return { verts, edges };
    };

    // --- Build wireframe octahedron ---
    const makeOcta = (cx, cy, cz, r) => {
      const verts = [
        {x:cx,   y:cy-r, z:cz},
        {x:cx+r, y:cy,   z:cz},
        {x:cx,   y:cy+r, z:cz},
        {x:cx-r, y:cy,   z:cz},
        {x:cx,   y:cy,   z:cz+r},
        {x:cx,   y:cy,   z:cz-r},
      ];
      const edges = [
        [0,1],[1,2],[2,3],[3,0],
        [0,4],[1,4],[2,4],[3,4],
        [0,5],[1,5],[2,5],[3,5],
      ];
      return { verts, edges };
    };

    // --- Particles ---
    const particles = Array.from({ length: 120 }, () => ({
      x: (Math.random() - 0.5) * 1200,
      y: (Math.random() - 0.5) * 900,
      z: (Math.random() - 0.5) * 800,
      r: Math.random() * 1.5 + 0.5,
      speed: Math.random() * 0.3 + 0.1,
    }));

    // --- Shapes (cubes + octahedra) ---
    const shapes = [
      { ...makeCube(  0,   0, -200, 120), color: '#00d4ff', rotSpeed: { x: 0.004, y: 0.007, z: 0.002 } },
      { ...makeOcta(-300, -180, -100, 80), color: '#7b2fff', rotSpeed: { x: 0.006, y: 0.004, z: 0.003 } },
      { ...makeOcta( 350,  200, -150, 60), color: '#ff2d78', rotSpeed: { x: 0.003, y: 0.008, z: 0.005 } },
      { ...makeCube(-250,  220, -300, 80), color: '#00ffaa', rotSpeed: { x: 0.005, y: 0.003, z: 0.006 } },
      { ...makeCube( 280, -200, -250, 90), color: '#7b2fff', rotSpeed: { x: 0.007, y: 0.005, z: 0.004 } },
    ];

    // Per-shape rotation accumulators
    const rots = shapes.map(() => ({ x: 0, y: 0, z: 0 }));

    const draw = () => {
      time += 0.012;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Mouse-driven global tilt
      const globalRX = mouseY * 0.08;
      const globalRY = mouseX * 0.08;

      // --- Draw particles ---
      particles.forEach(p => {
        let { x, y, z } = p;
        // Slow drift
        p.z += p.speed;
        if (p.z > 400) p.z = -400;

        // Apply global rotation
        let ry = rotateX(y, z, globalRX);
        let rx = rotateY(x, ry.z, globalRY);
        const proj = project(rx.x, ry.y, ry.z);
        if (proj.scale <= 0) return;

        const alpha = Math.max(0, Math.min(0.6, proj.scale * 0.7));
        ctx.beginPath();
        ctx.arc(proj.x, proj.y, p.r * proj.scale, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 212, 255, ${alpha})`;
        ctx.fill();
      });

      // --- Draw wireframe shapes ---
      shapes.forEach((shape, si) => {
        rots[si].x += shape.rotSpeed.x + Math.sin(time * 0.5) * 0.001;
        rots[si].y += shape.rotSpeed.y + Math.cos(time * 0.3) * 0.001;

        // Transform vertices
        const transformed = shape.verts.map(v => {
          let { x, y, z } = v;
          // Shape self-rotation
          let rx = rotateX(y, z, rots[si].x);
          let ry = rotateY(x, rx.z, rots[si].y);
          // Global mouse tilt
          let gx = rotateX(rx.y, ry.z, globalRX);
          let gy = rotateY(ry.x, gx.z, globalRY);
          return project(gy.x, gx.y, gx.z);
        });

        // Draw edges
        const color = shape.color;
        shape.edges.forEach(([a, b]) => {
          const va = transformed[a];
          const vb = transformed[b];
          if (va.scale <= 0 || vb.scale <= 0) return;

          const alpha = Math.min(0.35, (va.scale + vb.scale) / 2 * 0.4);
          ctx.beginPath();
          ctx.moveTo(va.x, va.y);
          ctx.lineTo(vb.x, vb.y);
          ctx.strokeStyle = color;
          ctx.globalAlpha = alpha;
          ctx.lineWidth = 1;
          ctx.stroke();
        });

        // Glow dot on each vertex
        transformed.forEach(v => {
          if (v.scale <= 0) return;
          ctx.beginPath();
          ctx.arc(v.x, v.y, 2 * v.scale, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.globalAlpha = Math.min(0.6, v.scale * 0.5);
          ctx.fill();
        });
      });

      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  return <canvas ref={canvasRef} className="bg3d-canvas" />;
};

export default Background3D;
